import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * GodRaysBackground
 * Fixed full-viewport WebGL layer: ocean-blue volumetric god rays.
 *
 * Props:
 *  intensity        number  (0..2)   default 1.0
 *  speed            number  (0..3)   default 1.0
 *  lightPosition    [x, y]           default [-0.15, 1.1]  (top-left, slightly off-screen)
 *  palette          { a, b, c }      three hex colors (deep, mid, highlight)
 *  overlayOpacity   number  (0..1)   default 0.35
 *  fadeIn           boolean          default true
 *  rayCount         number           default 7.0
 *  zIndex           number           default -1
 */
const DEFAULT_PALETTE = {
  a: "#0A1A2F", // deep ocean navy
  b: "#1E4C8A", // mid cobalt
  c: "#6FB6E8", // soft cyan highlight
};

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

// Fragment shader: radial god rays, slow drift, simplex-ish noise, grain.
const fragmentShader = /* glsl */ `
  precision highp float;

  varying vec2 vUv;

  uniform float uTime;
  uniform vec2  uResolution;
  uniform vec2  uLightPos;        // in UV space
  uniform float uIntensity;
  uniform float uRayCount;
  uniform vec3  uColorA;          // deep
  uniform vec3  uColorB;          // mid
  uniform vec3  uColorC;          // highlight
  uniform float uFlash;           // 0..1 lightning flash envelope
  uniform float uBoltX;           // 0..1 UV x of current bolt
  uniform float uBoltSeed;        // random seed per strike

  // --- hash + value noise (cheap, good enough for streak modulation) ---
  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  float fbm(vec2 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 5; i++) {
      v += amp * vnoise(p);
      p *= 2.02;
      amp *= 0.5;
    }
    return v;
  }

  // Luma-preserving blend of three palette stops by intensity in [0,1].
  vec3 palette3(float t) {
    t = clamp(t, 0.0, 1.0);
    vec3 lo = mix(uColorA, uColorB, smoothstep(0.0, 0.55, t));
    vec3 hi = mix(lo, uColorC, smoothstep(0.55, 1.0, t));
    return hi;
  }

  void main() {
    // Aspect-corrected UV for geometry calculations.
    vec2 uv = vUv;
    float aspect = uResolution.x / max(uResolution.y, 1.0);

    // Base radial field from light source.
    vec2 toLight = uv - uLightPos;
    vec2 toLightA = vec2(toLight.x * aspect, toLight.y);
    float dist = length(toLightA);

    // Angle around the light source — rays live in angular coordinates.
    float ang = atan(toLightA.y, toLightA.x);

    // Slow drift on angle + global pulse — "lighting flowing across".
    float driftA = 0.18 * sin(uTime * 0.08);
    float driftB = 0.12 * cos(uTime * 0.055);
    float a = ang + driftA + driftB;

    // Angular streak pattern — multiple frequencies interleaved so rays
    // feel organic, not stripey.
    float r1 = sin(a * uRayCount + uTime * 0.25);
    float r2 = sin(a * (uRayCount * 1.73) - uTime * 0.17 + 1.3);
    float r3 = sin(a * (uRayCount * 0.47) + uTime * 0.11 - 0.8);
    float streak = (r1 * 0.55 + r2 * 0.30 + r3 * 0.25);
    streak = 0.5 + 0.5 * streak;              // -> [0,1]
    streak = pow(streak, 1.6);                // sharpen hotspots, keep wide

    // Low-frequency noise modulates streak intensity along the ray so beams
    // feel volumetric, not geometric.
    vec2 np = vec2(a * 1.6, dist * 2.4) + vec2(uTime * 0.05, uTime * 0.03);
    float n = fbm(np);
    streak *= mix(0.65, 1.35, n);

    // Radial falloff — brighter near the light, fades into the deep.
    // Softer decay so beams reach deep across the viewport.
    float falloff = exp(-dist * 0.75);
    float halo    = exp(-dist * 2.4) * 0.9;

    // Combine into a 0..1-ish intensity field.
    float rays = streak * falloff * 1.8;
    float field = clamp(halo * 0.95 + rays * uIntensity, 0.0, 1.6);

    // Soft base gradient so the far side of the screen isn't flat black.
    float baseGrad = mix(0.02, 0.30, smoothstep(1.6, 0.1, dist));
    float t = clamp(field + baseGrad, 0.0, 1.0);

    vec3 col = palette3(t);

    // Deep vignette on the opposite corner — pushes depth.
    float vign = smoothstep(1.65, 0.4, length((uv - vec2(0.5)) * vec2(aspect, 1.0)));
    col *= mix(0.80, 1.0, vign);

    // Subtle film grain to kill banding on the gradient regions.
    float grain = (hash(gl_FragCoord.xy + uTime) - 0.5) * 0.025;
    col += grain;

    // --- lightning ------------------------------------------------------
    // Jagged vertical bolt: perturb a vertical line by multi-octave noise.
    // The line only renders when uFlash > 0 (JS drives the strike envelope).
    if (uFlash > 0.001) {
      vec2 bp = vec2(uBoltSeed * 7.1, uv.y * 6.0);
      float wob =
          (fbm(bp) - 0.5) * 0.14
        + (fbm(bp * 2.3 + 11.0) - 0.5) * 0.08
        + (fbm(bp * 5.7 + 23.0) - 0.5) * 0.04;
      // bolt gets wider as it falls (classic lightning shape)
      float widthScale = mix(0.006, 0.018, uv.y);
      float boltLineX = uBoltX + wob;
      float d = abs(uv.x - boltLineX) / widthScale;

      // Core + outer glow
      float core = exp(-d * d * 0.9);
      float glow = exp(-d * 0.3) * 0.55;
      // Fade bolt as it approaches the bottom of the screen
      float yFade = smoothstep(-0.05, 0.3, uv.y) * (1.0 - smoothstep(0.78, 1.05, uv.y));
      float boltAmt = (core + glow) * yFade * uFlash;

      // Branches: a couple of short diagonal spurs near mid-height
      float branchY = 0.55;
      if (uv.y > branchY - 0.12 && uv.y < branchY + 0.02) {
        float by = (uv.y - branchY) * 8.0;
        float bx = uv.x - (boltLineX + 0.06 * sign(sin(uBoltSeed * 31.0)));
        float bd = abs(bx - by * 0.4) / 0.012;
        boltAmt += exp(-bd * bd * 1.2) * 0.8 * uFlash * yFade;
      }

      // Cool white-cyan bolt
      vec3 boltCol = mix(uColorC, vec3(1.0), 0.65);
      col += boltCol * boltAmt * 2.4;

      // Overall ambient flash — brief hemispheric brighten, strongest near the bolt.
      float ambientFlash = uFlash * (0.35 + 0.45 * exp(-abs(uv.x - uBoltX) * 1.6));
      col += mix(uColorB, uColorC, 0.75) * ambientFlash;
    }
    // --------------------------------------------------------------------

    // Gentle tone shaping — keep it calm, no clipping.
    col = col / (col + vec3(0.9));
    col = pow(col, vec3(0.92));

    gl_FragColor = vec4(col, 1.0);
  }
`;

function hexToVec3(hex) {
  const c = new THREE.Color(hex);
  return new THREE.Vector3(c.r, c.g, c.b);
}

export const GodRaysBackground = ({
  intensity = 1.0,
  speed = 1.0,
  lightPosition = [0.05, 1.08],
  palette = DEFAULT_PALETTE,
  overlayOpacity = 0.35,
  fadeIn = true,
  rayCount = 7.0,
  zIndex = -1,
}) => {
  const mountRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // --- Three.js setup ---
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(window.devicePixelRatio); // raw DPR per user request
    renderer.setSize(mount.clientWidth, mount.clientHeight, false);
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    mount.appendChild(renderer.domElement);

    const uniforms = {
      uTime: { value: 0 },
      uResolution: {
        value: new THREE.Vector2(mount.clientWidth, mount.clientHeight),
      },
      uLightPos: {
        value: new THREE.Vector2(lightPosition[0], lightPosition[1]),
      },
      uIntensity: { value: intensity },
      uRayCount: { value: rayCount },
      uColorA: { value: hexToVec3(palette.a) },
      uColorB: { value: hexToVec3(palette.b) },
      uColorC: { value: hexToVec3(palette.c) },
      uFlash: { value: 0 },
      uBoltX: { value: 0.5 },
      uBoltSeed: { value: 0 },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms,
      depthTest: false,
      depthWrite: false,
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    scene.add(quad);

    // --- resize ---
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(w, h, false);
      uniforms.uResolution.value.set(w, h);
    };
    window.addEventListener("resize", onResize);

    // --- reduced-motion static fallback ---
    if (prefersReducedMotion) {
      uniforms.uTime.value = 0.0;
      renderer.render(scene, camera);
    } else {
      const start = performance.now();
      const baseLightX = lightPosition[0];
      const baseLightY = lightPosition[1];

      // --- lightning scheduler ---
      // A strike = short envelope with a few flickers. Random interval between strikes.
      let nextStrikeAt = (performance.now() - start) / 1000 + 1.2 + Math.random() * 2;
      let strike = null; // { startT, duration, flickers:[{at, amp, dur}] }

      const scheduleNextStrike = (nowT) => {
        nextStrikeAt = nowT + 2.5 + Math.random() * 4.5; // every 2.5–7s
      };

      const beginStrike = (nowT) => {
        const duration = 0.55 + Math.random() * 0.35;
        const flickerCount = 2 + Math.floor(Math.random() * 3); // 2–4 flickers
        const flickers = [];
        for (let i = 0; i < flickerCount; i++) {
          flickers.push({
            at: Math.random() * duration * 0.85,
            amp: 0.55 + Math.random() * 0.55,
            dur: 0.05 + Math.random() * 0.12,
          });
        }
        strike = {
          startT: nowT,
          duration,
          flickers,
          boltX: 0.1 + Math.random() * 0.8,
          seed: Math.random() * 100,
        };
        uniforms.uBoltX.value = strike.boltX;
        uniforms.uBoltSeed.value = strike.seed;
      };

      const sampleFlash = (localT, s) => {
        if (localT < 0 || localT > s.duration) return 0;
        let v = 0;
        for (const f of s.flickers) {
          const dt = localT - f.at;
          if (dt < 0 || dt > f.dur) continue;
          // Sharp attack, exp decay inside each flicker.
          const x = dt / f.dur;
          const env = Math.exp(-x * 5.0) * (1.0 - Math.exp(-x * 40.0));
          v = Math.max(v, env * f.amp);
        }
        return Math.min(v, 1.0);
      };

      const tick = (now) => {
        const t = ((now - start) / 1000) * speed;
        uniforms.uTime.value = t;

        // Slow drift of light source — "lighting flowing across".
        uniforms.uLightPos.value.set(
          baseLightX + Math.sin(t * 0.06) * 0.06,
          baseLightY + Math.cos(t * 0.045) * 0.04,
        );

        // Lightning
        if (!strike && t >= nextStrikeAt) {
          beginStrike(t);
        }
        if (strike) {
          const localT = t - strike.startT;
          const flash = sampleFlash(localT, strike);
          uniforms.uFlash.value = flash;
          if (localT > strike.duration) {
            strike = null;
            uniforms.uFlash.value = 0;
            scheduleNextStrike(t);
          }
        }

        renderer.render(scene, camera);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }

    // --- cleanup ---
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      quad.geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [intensity, speed, rayCount, lightPosition, palette]);

  return (
    <div
      aria-hidden="true"
      data-testid="god-rays-background"
      style={{
        position: "fixed",
        inset: 0,
        zIndex,
        pointerEvents: "none",
        overflow: "hidden",
        background: "#0A1A2F",
        animation: fadeIn ? "godrays-fade-in 1500ms ease-out both" : "none",
      }}
    >
      <div
        ref={mountRef}
        style={{ position: "absolute", inset: 0 }}
      />
      {/* Readability overlay */}
      <div
        data-testid="god-rays-overlay"
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg,
            rgba(5,15,30,${overlayOpacity * 0.85}) 0%,
            rgba(5,15,30,${overlayOpacity}) 55%,
            rgba(5,15,30,${overlayOpacity * 1.15}) 100%)`,
          pointerEvents: "none",
        }}
      />
      <style>{`
        @keyframes godrays-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default GodRaysBackground;
